import { getApiUggRoot } from "./apiHelpers";
import { useState, useEffect, useCallback } from "react";
import { useApolloClient, useQuery, useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_SUMMONER_PROFILE,
  GET_SUMMONER_PROFILE_SIMPLE,
  GET_SINGLE_MATCH_DATA,
  GET_INDIV_CHAMPION,
  GET_PLAYER_STATISTICS,
  GET_ADDITIONAL_MATCHES,
  GET_MATCH_TAGS,
  GET_MULTISEARCH,
  GET_SUMMONER_RANK_SNAPSHOTS,
  GET_PLAYER_RANKS,
  GET_PLAYER_LP,
  GET_HISTORIC_RANKS,
  GET_PLAYER_OVERALL_RANKING,
  GET_PERFORMANCE_SCORE,
  SET_PROFILE_CUSTOMIZATIONS,
} from "api/graphql/queries.js";
import { replacePlusFromURLPath } from "lib/general-helper";
import { CURRENT_SEASON_ID } from "lib/constants";

import { OVERVIEW_DEFAULT, MATCH_HISTORY_DEFAULT, CHAMPION_STATS_DEFAULT } from "query_parameters/filter_options_profile";
import { useUserSummonerName } from "lib/hooks/userHooks";

import { getApiRole, isRankedQueue } from "lib/general-helper";
import { roleMapping, queueTypeMapping } from "api/apiHelpers.js";
import { getRiotAssetsContext } from "@outplayed/riot-assets";

export function getDefaultValsForSummonerProfile(region, summonerName, options = {}, page = {}) {
  const { getChampionIdByName } = getRiotAssetsContext();
  let params = Object.assign({}, OVERVIEW_DEFAULT, options);

  const apiParams = {
    regionId: region || "",
    summonerName: summonerName ? replacePlusFromURLPath(summonerName.toLowerCase()) : "",
    queueType: String(queueTypeMapping(params.queueType)),
    role: String(roleMapping(getApiRole(params.role))),
    seasonId: String(params.season || CURRENT_SEASON_ID),
    seasonIdInt: parseInt(params.season || CURRENT_SEASON_ID),
    championId: (params.champion && getChampionIdByName(params.champion)) || "-1",
  };

  if (params.queueType === "normal_aram" || params.queueType === "nexus_blitz") {
    apiParams.role = "6";
  }

  return { apiParams, params };
}

export function useSummonerProfile(region, summonerName, options, page, queryParams = {}) {
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options, page);

  return useQuery(GET_SUMMONER_PROFILE, {
    variables: apiParams,
    ...queryParams,
  });
}

export function useSummonerProfileSimple(region, summonerName, options, page, queryParams = {}) {
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options, page);

  return useQuery(GET_SUMMONER_PROFILE_SIMPLE, {
    variables: {
      regionId: apiParams.regionId,
      summonerName: apiParams.summonerName,
      seasonIdInt: apiParams.seasonIdInt,
    },
    ...queryParams,
  });
}

export function useSummonerTags(summonerNames, region, championIds, queueType, queryParams = {}) {
  return useQuery(GET_MATCH_TAGS, {
    ...queryParams,
    variables: {
      summonerNames,
      regionId: region,
      championIds,
      queueTypes: summonerNames.map((el) => queueType || ""),
    },
  });
}

export const useLiveGameTags = (summonerNames, region, championIds, queueType, options) => {
  const { data, loading, error } = useSummonerTags(summonerNames, region, championIds, queueType, options);
  const summonerIndexed =
    data && Object.assign({}, ...data.fetchMultipleSummonerTags.map((player) => ({ [player.summonerName]: player.tagList })));
  return {
    tag_data: summonerIndexed,
    tag_loading: loading,
    tag_error: error,
  };
};

export function useProfileHistoricRanks(summonerName, regionId, queueType, queryParams = {}) {
  return useQuery(GET_HISTORIC_RANKS, {
    ...queryParams,
    variables: {
      summonerName,
      regionId,
      queueType,
    },
  });
}

export function useProfileOverallRanking(summonerName, regionId, queueType, queryParams = {}) {
  return useQuery(GET_PLAYER_OVERALL_RANKING, {
    ...queryParams,
    variables: {
      summonerName,
      regionId,
      queueType,
    },
  });
}

export function useSummonerRankSnapshots(regionId, summonerName, queryParams = {}) {
  return useQuery(GET_SUMMONER_RANK_SNAPSHOTS, {
    ...queryParams,
    variables: {
      regionId,
      summonerName,
      queueType: [420, 440],
    },
  });
}

export function useProfileRanks(summonerName, regionId, { seasonId } = {}, queryParams = {}) {
  return useQuery(GET_PLAYER_RANKS, {
    ...queryParams,
    variables: {
      summonerName,
      regionId,
      seasonId: seasonId || CURRENT_SEASON_ID,
    },
  });
}

export function usePlayerLP(summonerName, regionId, { queueType, patchVersion, timeframe } = {}, queryParams = {}) {
  return useQuery(GET_PLAYER_LP, {
    ...queryParams,
    variables: {
      summonerName,
      regionId,
      timeframe: timeframe || "seasonal",
      // queueType: queueType || 420,
      patchVersion: patchVersion || "12_1",
    },
  });
}

// Did not use useLazyQuery because it has a strange behavior when component rerenders
// Will function similiar to useLazyQuery while ignoring weird rerender behavior
export function useManualSummonerProfile(region, summonerName, options, page, queryParams = {}) {
  const client = useApolloClient();
  const [fetchState, setFetchState] = useState({ loading: false, data: null, error: null });
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options, page);

  const getSummoner = () => {
    setFetchState({ loading: true, data: null, error: null });
    client
      .query({
        query: GET_SUMMONER_PROFILE,
        variables: apiParams,
        ...queryParams,
      })
      .then((response) => {
        setFetchState({ loading: false, data: response.data, error: null });
      })
      .catch((error) => {
        setFetchState({ loading: false, data: null, error });
      });
  };

  return [getSummoner, fetchState];
}

export function useManualSummonerProfileSimple(region, summonerName, options, page, queryParams = {}) {
  const client = useApolloClient();
  const [fetchState, setFetchState] = useState({ loading: false, data: null, error: null });
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options, page);

  const getSummoner = () => {
    setFetchState({ loading: true, data: null, error: null });
    client
      .query({
        query: GET_SUMMONER_PROFILE_SIMPLE,
        variables: apiParams,
        ...queryParams,
      })
      .then((response) => {
        setFetchState({ loading: false, data: response.data, error: null });
      })
      .catch((error) => {
        setFetchState({ loading: false, data: null, error });
      });
  };

  return [getSummoner, fetchState];
}

export function usePlayerStatistics(summonerName, regionId, { queueType, seasonId, role } = {}, queryParams = {}) {
  return useQuery(GET_PLAYER_STATISTICS, {
    ...queryParams,
    variables: {
      summonerName,
      regionId,
      role: role || 7,
      seasonId: seasonId || CURRENT_SEASON_ID,
      queueType: queueType || [],
    },
  });
}

export function useSummonerMatch(region, summonerName, patch, matchId, queryParams) {
  const apiParams = {
    regionId: region || "",
    summonerName: replacePlusFromURLPath(summonerName) || "",
    matchId: matchId || "",
    version: patch || "",
  };

  return useQuery(GET_SINGLE_MATCH_DATA, {
    variables: apiParams,
    ...queryParams,
  });
}

export function usePerformanceScore(region, summonerName, patch, matchId, options, queryParams) {
  const apiParams = {
    regionId: region || "",
    summonerName: replacePlusFromURLPath(summonerName) || "",
    matchId: matchId || "",
    version: patch || "",
  };

  return useQuery(GET_PERFORMANCE_SCORE, {
    variables: apiParams,
    ...queryParams,
  });
}

export function useMultisearch() {
  const client = useApolloClient();
  const [fetchState, setFetchState] = useState({
    loading: false,
    data: undefined,
    error: undefined,
    refetch: undefined,
  });

  const getMultisearch = (summoners, region) => {
    setFetchState({
      loading: true,
      data: undefined,
      error: undefined,
    });

    client
      .query({
        query: GET_MULTISEARCH,
        variables: {
          summonerName: summoners,
          regionId: summoners.map(() => region),
        },
      })
      .then((response) => {
        setFetchState({
          loading: false,
          data: response.data,
          error: undefined,
        });
      })
      .catch((error) => {
        setFetchState({
          loading: false,
          data: undefined,
          error: error,
        });
      });
  };

  return [getMultisearch, fetchState];
}

export function updateProfileCustomizations(newCustomizations, queryOptions = {}) {
  const userSummoner = useUserSummonerName();
  const { loading, data, error, variables } = useSummonerProfile(userSummoner.regionId, userSummoner.summonerName, {}, null);
  const { playerInfo, customizationData } = (data && data.profileInit) || {};

  const [setProfileCustomizations, mutationResults] = useMutation(SET_PROFILE_CUSTOMIZATIONS, {
    ...queryOptions,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_SUMMONER_PROFILE,
        variables,
        fetchPolicy: "network-only",
      },
    ],
  });

  return [
    () => {
      const combinedCustomizations = {
        summonerName: playerInfo.summonerName,
        regionId: playerInfo.regionId,
        ...customizationData,
        ...(newCustomizations || {}),
      };
      if (!loading) {
        setProfileCustomizations({ variables: combinedCustomizations });
      }
    },
    mutationResults,
  ];
}

export function useMatchPage(summonerName, regionId, version, matchId, queryOptions = {}) {
  return useQuery(GET_MATCH_PAGE, {
    ...queryOptions,
    variables: {
      summonerName,
      regionId,
      version,
      matchIdString: String(matchId),
      matchIdInt: parseInt(matchId),
    },
  });
}
